import { useCallback } from 'react';
import { useAppDispatch } from 'store/hooks';

const WEBSOCKET_URL = import.meta.env.VITE_APP_PALMETTO_WEBSOCKET_URL;
const POLL_TIME = 10000; // How often to poll the connection state
const RETRY_TIME = 30 * 60 * 1000; // timeout for retry
const RECONNECT_DELAY = 5000; // delay before reconnecting

type CallbackFunction = (data: unknown, roomName: string) => void;
type WsConnectValues = {
  userid: number;
  groupid: number;
  superadmin: boolean;
  access_token: string;
};

let ws: WebSocket | null = null;
let connectionActive = false;
const joinedRooms: Record<string, boolean> = {};
let pollInterval: NodeJS.Timeout | null = null;
let timeoutInterval: NodeJS.Timeout | null = null;
let isWsReady: boolean | null = null;
let connectionCallbacks: (() => void)[] = [];
const roomCallbacks: Record<string, CallbackFunction> = {};
let wsConnectValues: WsConnectValues | null = null;
let connectionStateChangeCallback: ((isActive: boolean) => void) | null = null;

const useWebSocket = () => {
  const dispatch = useAppDispatch();

  const setConnectionStateChangeCallback = useCallback((func: (isActive: boolean) => void) => {
    if (func) {
      connectionStateChangeCallback = func;
    }
  }, []);

  const pollConnection = useCallback(() => {
    if (ws) {
      const status = ws.readyState;
      connectionActive = status === WebSocket.OPEN;
      if (status !== WebSocket.OPEN) {
        stopPolling();
      } else {
        ws.send(
          JSON.stringify({
            action: 'pingpong',
          })
        );
      }
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
    if (connectionStateChangeCallback) {
      connectionStateChangeCallback(false);
    }
  }, []);

  const retry = useCallback(() => {
    if (connectionActive) {
      return;
    }
    if (ws) {
      ws.close();
      ws = null;
    }
    connectionActive = false;
    if (timeoutInterval) {
      clearInterval(timeoutInterval);
      timeoutInterval = null;
    }
    stopPolling();

    if (wsConnectValues) {
      connect(
        wsConnectValues.userid,
        wsConnectValues.groupid,
        wsConnectValues.superadmin,
        wsConnectValues.access_token
      );
    }
  }, [connectionActive, stopPolling]);

  const connect = useCallback(
    async (userid: number, groupid: number, superadmin: boolean, access_token: string) => {
      wsConnectValues = { userid, groupid, superadmin, access_token };
      if (connectionActive) {
        return;
      }

      try {
        ws = new WebSocket(
          `${WEBSOCKET_URL}?userid=${userid}&groupid=${groupid}&superadmin=${superadmin}&access_token=${access_token}`
        );
      } catch (err) {
        connectionActive = false;
        return;
      }

      if (connectionStateChangeCallback) {
        connectionStateChangeCallback(true);
      }

      ws.onopen = () => {
        connectionActive = true;
        isWsReady = true;
        connectionCallbacks.forEach((callback) => callback());
        connectionCallbacks = [];
        if (Object.keys(joinedRooms).length > 0) {
          ws!.send(
            JSON.stringify({
              action: 'join',
              data: {
                room: Object.keys(joinedRooms),
              },
            })
          );
        }
      };

      ws.onclose = () => {
        connectionActive = false;
        isWsReady = false;
        stopPolling();
        if (timeoutInterval) {
          clearInterval(timeoutInterval);
          timeoutInterval = null;
        }

        setTimeout(() => {
          retry();
        }, RECONNECT_DELAY);
      };

      ws.onmessage = (msg) => {
        if (msg) {
          let parsed = null;
          try {
            parsed = JSON.parse(msg.data);
          } catch (err) {
            return;
          }

          if (parsed.room === 'pong') {
            return;
          }

          if (parsed.room && roomCallbacks[parsed.room]) {
            roomCallbacks[parsed.room](parsed.data, parsed.room);
          } else {
            console.log('Room not in callbackRooms:', parsed.room);
          }
        }
      };

      pollInterval = setInterval(pollConnection, POLL_TIME);

      timeoutInterval = setTimeout(() => {
        retry();
      }, RETRY_TIME);
    },
    [connectionActive, pollConnection, retry, joinedRooms]
  );

  const joinRoom = useCallback(
    (room: string, callback: CallbackFunction) => {
      if (joinedRooms[room]) return;

      const joinAction = () => {
        if (ws && ws.readyState === 1) {
          ws.send(
            JSON.stringify({
              action: 'join',
              data: {
                room: [room],
              },
            })
          );
        }
      };
      if (isWsReady) {
        joinAction();
      } else {
        connectionCallbacks.push(joinAction);
      }

      roomCallbacks[room] = callback;
      joinedRooms[room] = true;
    },
    [joinedRooms, isWsReady]
  );

  const leaveRoom = useCallback(
    (room: string) => {
      if (!joinedRooms[room]) return;

      if (ws && ws.readyState === 1) {
        ws.send(
          JSON.stringify({
            action: 'leave',
            data: {
              room: [room],
            },
          })
        );
      }

      delete roomCallbacks[room];
      delete joinedRooms[room];
    },
    [joinedRooms]
  );

  const onConnect = useCallback((callback: () => void) => {
    if (isWsReady) {
      console.log('WS Connected:', joinedRooms);
      callback();
    } else {
      connectionCallbacks.push(callback);
    }
  }, []);

  return {
    connect,
    retry,
    setConnectionStateChangeCallback,
    joinRoom,
    connectionActive,
    leaveRoom,
    ws,
    onConnect,
  };
};

export default useWebSocket;
